import { Plus, X } from 'lucide-react';
import { Button, DatePicker } from '@roger/ui-components';
import { OCCURRENCE_CODES } from '../../../data/occurrenceCodes';
import { formatDateToISO, parseISOToLocalDate } from '../../../utils/dateUtils';
import { CodeCombobox } from '../claimWorksheet/CodeListField';
import type { OccurrenceRow } from './types';

export function OccurrenceCodesField({
  rows,
  onChange,
  maxRows = 10,
  readOnly = false,
}: {
  rows: OccurrenceRow[];
  onChange: (next: OccurrenceRow[]) => void;
  maxRows?: number;
  readOnly?: boolean;
}) {
  const update = (i: number, patch: Partial<OccurrenceRow>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, { code: '', date: '' }]);

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center gap-2 text-xs font-medium text-muted-foreground'>
        <span className='w-28'>Occurrence Code</span>
        <span className='w-36'>Occurrence Date</span>
      </div>
      {rows.map((entry, index) => (
        // Wraps rather than overflowing a phone-width dialog; unchanged at md.
        <div
          key={index}
          className='flex flex-wrap items-center gap-2 md:flex-nowrap'
        >
          <CodeCombobox
            value={entry.code}
            options={OCCURRENCE_CODES}
            disabled={readOnly}
            onChange={code => update(index, { code })}
          />
          <DatePicker
            datePickerSize='sm'
            typable
            className='w-36'
            disabled={readOnly}
            date={parseISOToLocalDate(entry.date)}
            onDateChange={date =>
              update(index, { date: date ? formatDateToISO(date) : '' })
            }
          />
          <Button
            type='button'
            variant='ghost'
            size='icon'
            aria-label='Remove occurrence code'
            disabled={readOnly}
            onClick={() => remove(index)}
          >
            <X className='h-4 w-4' />
          </Button>
        </div>
      ))}
      <Button
        type='button'
        variant='outline'
        size='sm'
        className='self-start'
        disabled={readOnly || rows.length >= maxRows}
        onClick={add}
      >
        <Plus className='mr-1 h-3 w-3' />
        Add Occurrence Code
      </Button>
    </div>
  );
}
